import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, CheckCircle2, Loader2, Info, LogIn, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useWorkforceIntelligence } from "@/contexts/WorkforceIntelligenceContext";
import BackNavigation from "@/components/BackNavigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industries = [
  "Manufacturing", "Logistics", "Security", "Healthcare", "Technology",
  "Government", "Education", "Construction", "Nonprofit", "Other"
];

const organizationSizes = [
  "1–49 employees", "50–249 employees", "250–999 employees", "1000+ employees"
];

const roles = [
  "Executive", "HR Leader", "Operations Leader", "Manager", "Educator", "Other"
];

const countries = [
  "United States", "Canada", "United Kingdom", "Germany", "France", 
  "Australia", "Japan", "Singapore", "India", "Brazil", "Mexico", "Other"
];

const surveyQuestions = {
  trust: {
    title: "Workforce Trust",
    description: "Assess the level of trust between employees and leadership.",
    questions: [
      { id: "q1", text: "Employees trust the decisions made by senior leadership." },
      { id: "q2", text: "Leadership communicates openly and transparently with employees." },
      { id: "q3", text: "Employees believe leadership acts in the organization's best interest." },
      { id: "q4", text: "Employees feel safe expressing concerns and ideas." },
      { id: "q5", text: "Leadership demonstrates integrity and accountability." },
      { id: "q6", text: "Leadership follows through on commitments made to employees." }
    ]
  },
  alignment: {
    title: "Organizational Alignment",
    description: "Evaluate strategic alignment across the organization.",
    questions: [
      { id: "q7", text: "Employees clearly understand the organization's strategic priorities." },
      { id: "q8", text: "Departments collaborate effectively toward shared goals." },
      { id: "q9", text: "Leadership decisions are aligned with the organization's mission." },
      { id: "q10", text: "Operational goals are consistent across departments." },
      { id: "q11", text: "Employees understand how their work contributes to strategic objectives." },
      { id: "q12", text: "Organizational priorities are communicated consistently across units." }
    ]
  },
  readiness: {
    title: "Workforce Readiness",
    description: "Measure workforce skills and adaptability.",
    questions: [
      { id: "q13", text: "The workforce has the skills required for current operations." },
      { id: "q14", text: "Employees receive adequate training and professional development." },
      { id: "q15", text: "The organization adapts effectively to workforce changes." },
      { id: "q16", text: "Workforce planning supports future operational needs." },
      { id: "q17", text: "The organization invests sufficiently in workforce capability." },
      { id: "q18", text: "Employees are prepared to respond to operational disruption or change." }
    ]
  },
  ai_capability: {
    title: "AI Workforce Capability",
    description: "Assess AI readiness and technology integration.",
    questions: [
      { id: "q19", text: "Employees understand how artificial intelligence will affect their work." },
      { id: "q20", text: "Leadership has a clear strategy for AI adoption." },
      { id: "q21", text: "The organization is investing in AI-related workforce skills." },
      { id: "q22", text: "Employees receive training related to AI and emerging technologies." },
      { id: "q23", text: "The organization is prepared to integrate AI into operations." },
      { id: "q24", text: "Leadership understands the risks and opportunities of AI adoption." }
    ]
  },
  resilience: {
    title: "Organizational Resilience",
    description: "Evaluate change management and adaptive capacity.",
    questions: [
      { id: "q25", text: "The organization manages change effectively." },
      { id: "q26", text: "Workforce morale remains strong during operational changes." },
      { id: "q27", text: "Leadership responds quickly to emerging challenges." },
      { id: "q28", text: "The workforce adapts effectively to technological disruptions." },
      { id: "q29", text: "The organization is prepared for future workforce transformation." },
      { id: "q30", text: "Workforce data is used to guide leadership decisions." }
    ]
  }
};

const sections = ["metadata", "trust", "alignment", "readiness", "ai_capability", "resilience"];

// Blue shades for section badges
const sectionColors = {
  trust: "bg-[#1E3A8A]",
  alignment: "bg-[#2563EB]",
  readiness: "bg-[#3B82F6]",
  ai_capability: "bg-[#60A5FA]",
  resilience: "bg-[#93C5FD] text-[#1E3A8A]"
};

export default function ObservatorySurvey() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { submitAssessment, isSaving } = useWorkforceIntelligence();
  const [currentSection, setCurrentSection] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const [metadata, setMetadata] = useState({
    industry: "",
    organization_size: "",
    role: "",
    country: "",
    state_region: "",
    organization_name: "",
    email: ""
  });

  const [responses, setResponses] = useState(
    Object.fromEntries(Array.from({ length: 30 }, (_, i) => [`q${i + 1}`, 5]))
  );

  // Pre-fill email if authenticated
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setMetadata(prev => ({ ...prev, email: user.email }));
    }
  }, [isAuthenticated, user]);

  const progress = ((currentSection + 1) / sections.length) * 100;

  const handleMetadataChange = (field, value) => {
    setMetadata(prev => ({ ...prev, [field]: value }));
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({ ...prev, [questionId]: value[0] }));
  };

  const validateMetadata = () => {
    return metadata.industry && metadata.organization_size && metadata.role && metadata.country;
  };

  const handleNext = () => {
    if (currentSection === 0 && !validateMetadata()) {
      toast.error("Please complete all required fields before continuing.");
      return;
    }
    setCurrentSection(prev => Math.min(prev + 1, sections.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        metadata: {
          industry: metadata.industry,
          organization_size: metadata.organization_size,
          role: metadata.role,
          country: metadata.country,
          state_region: metadata.state_region || null,
          organization_name: metadata.organization_name || null,
          email: metadata.email || null
        },
        responses
      };

      // Submit to existing observatory endpoint for legacy results
      const response = await axios.post(`${API}/observatory/survey`, payload);
      
      // Store legacy results for results page
      sessionStorage.setItem("surveyResultId", response.data.id);
      sessionStorage.setItem("surveyResult", JSON.stringify(response.data));
      
      // If authenticated, also save to WII profile for personalized features
      if (isAuthenticated) {
        try {
          await submitAssessment(responses, payload.metadata);
          toast.success("Assessment saved to your profile!");
        } catch (wiiError) {
          console.error("WII profile save error:", wiiError);
          // Continue anyway - legacy results are saved
          toast.info("Results saved. Log in to sync with your profile.");
        }
      } else {
        // Show prompt to save results
        setShowLoginPrompt(true);
      }
      
      toast.success("Diagnostic submitted successfully!");
      navigate("/observatories/global/results");
      
    } catch (error) {
      console.error("Survey submission error:", error);
      toast.error("Failed to submit diagnostic. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderMetadataSection = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#111827] mb-2">
          Organization Profile
        </h2>
        <p className="text-[#4B5563]">
          Help us understand your context for accurate benchmarking.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="industry">Industry Sector <span className="text-[#991B1B]">*</span></Label>
          <Select value={metadata.industry || undefined} onValueChange={(v) => handleMetadataChange("industry", v)}>
            <SelectTrigger id="industry" data-testid="select-industry" className="bg-white">
              <SelectValue placeholder="Select your industry" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {industries.map(industry => (
                <SelectItem key={industry} value={industry} className="cursor-pointer">{industry}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-size">Organization Size <span className="text-[#991B1B]">*</span></Label>
          <Select value={metadata.organization_size || undefined} onValueChange={(v) => handleMetadataChange("organization_size", v)}>
            <SelectTrigger id="org-size" data-testid="select-org-size" className="bg-white">
              <SelectValue placeholder="Select organization size" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {organizationSizes.map(size => (
                <SelectItem key={size} value={size} className="cursor-pointer">{size}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="role">Your Role <span className="text-[#991B1B]">*</span></Label>
          <Select value={metadata.role || undefined} onValueChange={(v) => handleMetadataChange("role", v)}>
            <SelectTrigger id="role" data-testid="select-role" className="bg-white">
              <SelectValue placeholder="Select your role" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {roles.map(role => (
                <SelectItem key={role} value={role} className="cursor-pointer">{role}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="country">Country <span className="text-[#991B1B]">*</span></Label>
          <Select value={metadata.country || undefined} onValueChange={(v) => handleMetadataChange("country", v)}>
            <SelectTrigger id="country" data-testid="select-country" className="bg-white">
              <SelectValue placeholder="Select your country" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {countries.map(country => (
                <SelectItem key={country} value={country} className="cursor-pointer">{country}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="state">State / Region</Label>
          <Input
            id="state"
            placeholder="e.g., California, Ontario"
            value={metadata.state_region}
            onChange={(e) => handleMetadataChange("state_region", e.target.value)}
            className="bg-white"
            data-testid="input-state"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="org-name">Organization Name (optional)</Label>
          <Input
            id="org-name"
            placeholder="Your organization name"
            value={metadata.organization_name}
            onChange={(e) => handleMetadataChange("organization_name", e.target.value)}
            className="bg-white"
            data-testid="input-org-name"
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="email">Email Address (optional)</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={metadata.email}
            onChange={(e) => handleMetadataChange("email", e.target.value)}
            className="bg-white"
            data-testid="input-email"
          />
          <p className="text-xs text-[#4B5563] flex items-center gap-1">
            <Info className="w-3 h-3" />
            Optional. May be used to send a copy of your diagnostic report.
          </p>
        </div>
      </div>
    </motion.div>
  );

  const renderQuestionSection = (sectionKey) => {
    const section = surveyQuestions[sectionKey];
    if (!section) return null;

    return (
      <motion.div
        key={sectionKey}
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-8"
      >
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${sectionColors[sectionKey]} text-white text-sm font-medium mb-4`}>
            Section {sections.indexOf(sectionKey)} of {sections.length - 1}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#111827] mb-2">
            {section.title}
          </h2>
          <p className="text-[#4B5563]">
            {section.description}
          </p>
        </div>

        <div className="space-y-6">
          {section.questions.map((question, index) => (
            <Card key={question.id} className="border-[#E5E7EB]" data-testid={`question-${question.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start gap-4 mb-6">
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full ${sectionColors[sectionKey]} text-white flex items-center justify-center font-mono text-sm font-medium`}>
                    {index + 1}
                  </span>
                  <p className="text-[#111827] font-medium pt-1">{question.text}</p>
                </div>
                
                <div className="px-4">
                  <Slider
                    value={[responses[question.id]]}
                    onValueChange={(value) => handleResponseChange(question.id, value)}
                    min={1}
                    max={10}
                    step={1}
                    className="mb-4"
                    data-testid={`slider-${question.id}`}
                  />
                  <div className="flex justify-between items-center text-xs text-[#4B5563]">
                    <span>1 - Strongly Disagree</span>
                    <span className="font-mono font-bold text-[#1E3A8A] text-2xl bg-blue-50 px-4 py-1 rounded-lg">{responses[question.id]}</span>
                    <span>10 - Strongly Agree</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-8 sm:py-12" data-testid="survey-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back Navigation */}
        <div className="mb-6">
          <BackNavigation fallbackPath="/observatories/global" fallbackLabel="Global Observatory" />
        </div>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-2" style={{ color: '#991B1B' }}>
            Workforce Intelligence Diagnostic
          </h1>
          <p className="text-[#4B5563]">
            30 questions · Estimated time: 5-7 minutes
          </p>
        </div>

        {/* Progress */}
        <Card className="mb-8 border-[#E5E7EB]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[#4B5563]">Progress</span>
              <span className="text-sm font-mono text-[#1E3A8A]">{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} className="h-2 mb-4" data-testid="survey-progress" />
            <div className="flex justify-center gap-2">
              {sections.map((section, index) => (
                <div
                  key={section}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-mono transition-all ${
                    index < currentSection
                      ? "bg-[#1E3A8A] text-white"
                      : index === currentSection
                      ? "bg-[#3B82F6] text-white"
                      : "bg-[#E5E7EB] text-[#4B5563]"
                  }`}
                  data-testid={`progress-step-${index}`}
                >
                  {index < currentSection ? <CheckCircle2 className="w-4 h-4" /> : index + 1}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Survey Content */}
        <Card className="border-[#E5E7EB] shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <AnimatePresence mode="wait">
              {currentSection === 0 && renderMetadataSection()}
              {currentSection > 0 && renderQuestionSection(sections[currentSection])}
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t border-[#E5E7EB]">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className="gap-2 border-[#E5E7EB]"
                data-testid="prev-button"
              >
                <ArrowLeft className="w-4 h-4" />
                Previous
              </Button>

              {currentSection < sections.length - 1 ? (
                <Button onClick={handleNext} className="gap-2 bg-[#1E3A8A] text-white hover:bg-[#1E40AF]" data-testid="next-button">
                  Next Section
                  <ArrowRight className="w-4 h-4" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="gap-2 bg-[#991B1B] text-white hover:bg-[#7F1D1D]"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      Submit Diagnostic
                      <CheckCircle2 className="w-4 h-4" />
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
